import type { FC } from 'react';
import React, { Suspense, useEffect } from 'react';
import { Amplify } from 'aws-amplify';
import { Authenticator } from '@aws-amplify/ui-react';
import config from 'config';
import { Navigate, Outlet, RouterProvider } from 'react-router-dom';
import { createBrowserRouter } from '@datadog/browser-rum-react/react-router-v6';
import { Titled } from 'react-titled';
import { SnackbarProvider } from 'notistack';
import queryString from 'query-string';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import GlobalErrorBoundary from 'GlobalErrorBoundary';
import { queryClient } from 'lib/react-query';
import AuthRequired from 'lib/auth/AuthRequired';
import ProgressSkeleton from 'components/layout/ProgressSkeleton';
import { Layout } from 'components/layout';
import theme from './theme';
import { EmergencyProvider } from 'features/emergency';
import { broadcastQueryClient } from '@tanstack/query-broadcast-client-experimental';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import NetworkProvider from './features/networks/components/NetworkProvider';
import './index.css';
import { Forbidden } from 'features/users';
import { lazy } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import ThemeProvider from '@mui/system/ThemeProvider';
import { SyncAirflowStore } from 'features/airflow';

const Login = lazy(() => import('pages/login'));
const LoginAutoTest = lazy(() => import('pages/login-autotest'));
const RulesPage = lazy(() => import('pages/rules'));
const UsersPage = lazy(() => import('./pages/users'));
const RuleDetailsPage = lazy(() => import('./pages/rules/RuleDetailsPage'));
const MetricsPage = lazy(() => import('pages/metrics'));
const RuleDuplicatePage = lazy(() => import('pages/rules/RuleDuplicatePage'));
const RuleEditPage = lazy(() => import('pages/rules/RuleEditPage'));
const LogsPage = lazy(() => import('pages/logs'));
const NetworksPage = lazy(() => import('./pages/network'));
const RulesHistoryPage = lazy(() => import('pages/rule-history'));
Amplify.configure({
  Auth: {
    mandatorySignIn: true,
    region: config.REGION,
    userPoolId: config.COGNITO_USER_POOL_ID,
    userPoolWebClientId: config.COGNITO_APP_CLIENT_ID,
    oauth: {
      domain: config.COGNITO_DOMAIN,
      scope: ['email', 'openid', 'profile'],
      redirectSignIn: config.COGNITO_REDIRECT_SIGN_IN ?? `${window.location.origin}/`,
      redirectSignOut: config.COGNITO_REDIRECT_SIGN_OUT ?? `${window.location.origin}/`,
      responseType: 'code',
    },
  },
});

const persister = createAsyncStoragePersister({
  storage: window.localStorage,
});

broadcastQueryClient({
  queryClient,
});

const clearCache = async () => {
  await queryClient.cancelQueries();
  await persister.removeClient();
  await queryClient.invalidateQueries();
};

const Providers: FC = () => {
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    window.addEventListener('beforeunload', clearCache);
    return () => {
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      window.removeEventListener('beforeunload', clearCache);
    };
  }, []);

  return (
    <QueryParamProvider
      adapter={ReactRouter6Adapter}
      options={{
        searchStringToObject: (str) => queryString.parse(str, { arrayFormat: 'comma' }),
        objectToSearchString: (input) => queryString.stringify(input, { arrayFormat: 'comma' }),
      }}
    >
      <CssBaseline />
      <Authenticator.Provider>
        <PersistQueryClientProvider client={queryClient} persistOptions={{ persister }}>
          <ThemeProvider theme={theme}>
            <GlobalErrorBoundary>
              <SnackbarProvider
                autoHideDuration={1000}
                maxSnack={5}
                anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
                preventDuplicate
              >
                <EmergencyProvider>
                  <Suspense fallback={<ProgressSkeleton />}>
                    <Outlet />
                  </Suspense>
                </EmergencyProvider>
              </SnackbarProvider>
            </GlobalErrorBoundary>
          </ThemeProvider>
          <ReactQueryDevtools />
        </PersistQueryClientProvider>
      </Authenticator.Provider>
    </QueryParamProvider>
  );
};

const router = createBrowserRouter([
  {
    element: (
      <Titled title="AutoRules">
        <Providers />
      </Titled>
    ),
    children: [
      {
        path: '/networks',
        element: (
          <AuthRequired>
            <SyncAirflowStore />
            <NetworkProvider>
              <Outlet />
            </NetworkProvider>
          </AuthRequired>
        ),
        children: [
          {
            index: true,
            path: '',
            element: (
              <Titled title="Networks">
                <NetworksPage />
              </Titled>
            ),
          },
          {
            path: ':network/rules',
            children: [
              {
                index: true,
                path: '',
                element: (
                  <Titled title="Rules">
                    <Layout className="px-0">
                      <RulesPage />
                    </Layout>
                  </Titled>
                ),
              },
              {
                path: 'details/:ruleId',
                element: (
                  <Titled title="Rule details">
                    <Layout>
                      <RuleDetailsPage />
                    </Layout>
                  </Titled>
                ),
              },
              {
                path: 'edit/:ruleId',
                element: (
                  <Titled title="Edit rule">
                    <Layout>
                      <RuleEditPage />
                    </Layout>
                  </Titled>
                ),
              },
              {
                path: 'duplicate/:ruleId',
                element: (
                  <Titled title="Duplicate rule">
                    <Layout>
                      <RuleDuplicatePage />
                    </Layout>
                  </Titled>
                ),
              },
            ],
          },
          {
            path: ':network/metrics',
            element: (
              <AuthRequired>
                <Titled title="Metrics">
                  <Layout>
                    <MetricsPage />
                  </Layout>
                </Titled>
              </AuthRequired>
            ),
          },
        ],
      },
      {
        path: '/login',
        element: (
          <Titled title="Login">
            <Login />
          </Titled>
        ),
      },
      {
        path: '/login/autotest',
        element: (
          <Titled title="Login AutoTest">
            <LoginAutoTest />
          </Titled>
        ),
      },
      {
        path: '/users',
        element: (
          <AuthRequired>
            <Titled title="Users">
              <Layout>
                <UsersPage />
              </Layout>
            </Titled>
          </AuthRequired>
        ),
      },
      {
        path: '/rule-history',
        element: (
          <AuthRequired>
            <Titled title="History">
              <Layout>
                <RulesHistoryPage />
              </Layout>
            </Titled>
          </AuthRequired>
        ),
      },
      {
        path: '/logs',
        element: (
          <AuthRequired>
            <Titled title="Logs">
              <Layout>
                <LogsPage />
              </Layout>
            </Titled>
          </AuthRequired>
        ),
      },
      {
        path: '/forbidden',
        element: <Forbidden />,
      },
      { path: '*', element: <Navigate to="/networks" replace /> },
    ],
  },
]);

const App: FC = () => (
  <React.StrictMode>
    <RouterProvider router={router} />
  </React.StrictMode>
);

export default App;
